import { useEffect, useRef, useState } from 'react';
import { agentCommand, likelyShell, type Shell } from '../../lib/agent-command';
import { codexCommand } from '../../lib/codex-command';
import { rememberMeasuredClient, storedMeasuredClient, type MeasuredClient, type MeasuredModel } from '../../lib/measured-client';
import { connectBridge, disconnectBridge } from '../../lib/agent-bridge';
import { measurementSetupCopy } from '../../lib/strings/measurement-setup';
import { useLocale } from '../../state/useLocale';
import { Button } from '../../ui';
import { agentCall, Copy } from './AgentKeys';
import { useBridge } from '../play/AgentStrip';

// Keys belong to the account that made them, including a guest account.
// Never reuse another account's command after signing out in the same tab.
const storageKey = (userId: string) => `engineeros.measuredAgentKey.${userId}`;
function storedKey(userId: string): string | null {
  try { return sessionStorage.getItem(storageKey(userId)); } catch { return null; }
}

/** The owner explicitly prepares a measured client. Receiving usage, not this button, proves readiness. */
export function MeasuredAgentSetup({ userId, target = 'server', model, client: fixedClient, smallerModel = false, measuredModel }: { userId: string; target?: 'tab' | 'server'; model?: 'haiku'; client?: MeasuredClient; smallerModel?: boolean; measuredModel?: MeasuredModel }) {
  const { locale } = useLocale();
  const s = measurementSetupCopy[locale];
  const [key, setKey] = useState<string | null>(() => storedKey(userId));
  const [keyOwner, setKeyOwner] = useState(userId);
  const [shell, setShell] = useState<Shell>(() => likelyShell(navigator.platform));
  const [preferredClient, setPreferredClient] = useState<{ owner: string; client: MeasuredClient }>(() => ({ owner: userId, client: storedMeasuredClient(userId) }));
  const client = measuredModel?.client ?? fixedClient ?? (preferredClient.owner === userId ? preferredClient.client : storedMeasuredClient(userId));
  const clientFixed = !!fixedClient || !!measuredModel;
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<number | null>(null);
  const [preparedTab, setPreparedTab] = useState<string | null>(null);
  const bridge = useBridge();
  const currentUser = useRef(userId);
  currentUser.current = userId;
  const currentTarget = useRef(target);
  currentTarget.current = target;

  useEffect(() => {
    setKey(storedKey(userId));
    setKeyOwner(userId);
    setProblem(null);
    setBusy(false);
    setPreparedTab(null);
    setPreferredClient({ owner: userId, client: storedMeasuredClient(userId) });
  }, [userId, target]);

  const prepareTarget = (owner: string) => {
    if (target === 'tab') {
      // The command must reach the same VM that this page grades. The desk
      // must never lend or disconnect another browser workspace implicitly.
      try {
        // This explicit owner action refreshes the socket's session too: a
        // socket left over from another account must not be mistaken for it.
        disconnectBridge();
        connectBridge();
        setPreparedTab(owner);
      } catch {
        setProblem(0);
      }
    }
  };

  const prepare = async () => {
    if (busy) return;
    const owner = userId;
    const place = target;
    const kept = storedKey(owner);
    if (kept) { setKey(kept); setKeyOwner(owner); prepareTarget(owner); return; }
    setBusy(true);
    setProblem(null);
    const { status, data } = await agentCall('/api/agent/keys', { name: client === 'codex' ? 'Codex optimization' : 'Claude Code optimization' });
    if (currentUser.current !== owner || currentTarget.current !== place) return;
    setBusy(false);
    if (status !== 201 || typeof data?.secret !== 'string' || !data.secret) {
      setProblem(status);
      return;
    }
    try { sessionStorage.setItem(storageKey(owner), data.secret); } catch { /* Kept in memory for this mount. */ }
    setKey(data.secret);
    setKeyOwner(owner);
    prepareTarget(owner);
  };

  const tabReady = target !== 'tab' || (preparedTab === userId && bridge.state === 'on');
  const command = key && keyOwner === userId && tabReady
    ? client === 'codex' ? codexCommand(window.location.origin, key, shell, smallerModel, measuredModel?.client === 'codex' ? measuredModel.id : undefined)
      : agentCommand(window.location.origin, key, shell) + (measuredModel?.client === 'claude' && /^[a-z0-9._:-]+$/i.test(measuredModel.id) ? ` --model ${measuredModel.id}` : smallerModel || model === 'haiku' ? ' --model haiku' : '')
    : null;
  return (
    <section className="block measurement-setup" aria-label={s.title}>
      <h3 className="label">{s.title}</h3>
      <p>{s.body}</p>
      <div role="group" aria-label={s.client}>
        {(['claude', 'codex'] as const).map((option) => <Button key={option} variant="quiet" aria-pressed={client === option} disabled={busy || clientFixed} onClick={() => { rememberMeasuredClient(userId, option); setPreferredClient({ owner: userId, client: option }); }}>{option === 'claude' ? 'Claude Code' : 'Codex'}</Button>)}
      </div>
      {clientFixed && <p className="note">{s.sameClient}</p>}
      {measuredModel && <p className="machine" dir="ltr">{measuredModel.id}</p>}
      {client === 'codex' && <p>{s.codex}</p>}
      {target === 'server' && <p className="note">{s.server}</p>}
      {target === 'tab' && preparedTab === userId && !tabReady && <p role="status">{s.tabConnecting}</p>}
      {!command ? <Button variant="solid" disabled={busy} onClick={() => void prepare()}>{busy ? s.making : s.make}</Button> : <>
        <p role="status">{s.ready}</p>
        {target === 'tab' && <p>{s.tabConnected}</p>}
        <div role="group" aria-label={s.shell}>
          <Button variant="quiet" aria-pressed={shell === 'posix'} onClick={() => setShell('posix')}>{s.posix}</Button>
          <Button variant="quiet" aria-pressed={shell === 'powershell'} onClick={() => setShell('powershell')}>{s.powershell}</Button>
        </div>
        <div className="aikey__codewrap"><pre dir="ltr" className="aikey__code"><code>{command}</code></pre><Copy text={command} /></div>
        <p>{s.verify}</p>
        <p>{s.fresh}</p>
        <p className="note">{s.key}</p>
      </>}
      {problem !== null && <p role="alert">{problem === 409 ? s.tooMany : problem === 401 ? s.expired : s.failed}</p>}
      <p className="note">{s.other}</p>
      <a href="/agent">{s.manage}</a>
    </section>
  );
}
